import React, { useState, useEffect, useRef, useCallback } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import Landing from './Landing'
import Dashboard from './Dashboard'
import Budget from './Budget'
import Photos from './Photos'
import Lists from './Lists'
import Calendar from './Calendar'
import Fitness from './Fitness'
import DailyLog from './DailyLog'
import Social from './Social'
import './App.css'
import Avatar from './Avatar'
import { useAvatars } from './useAvatars'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

// All pages in swipe order
const SWIPE_ORDER = [
  'dashboard',
  'daily',
  'fitness',
  'social',
  'calendar',
  'budget',
  'photos',
  'lists',
]

const NAV_ITEMS = [
  { id: 'dashboard', label: 'Home', icon: '🏠' },
  { id: 'daily', label: 'Daily', icon: '📊' },
  { id: 'fitness', label: 'Fitness', icon: '💪' },
  { id: 'social', label: 'Social', icon: '💬' },
  { id: 'more', label: 'More', icon: '••' },
]

const MORE_ITEMS = [
  { id: 'calendar', label: 'Calendar', icon: '📅' },
  { id: 'budget', label: 'Budget', icon: '💰' },
  { id: 'photos', label: 'Photos', icon: '📷' },
  { id: 'lists', label: 'Lists', icon: '📝' },
]

// Swipe hook — returns touch handlers and a slide state for animation
function useSwipeNav(activePage, onNavigate) {
  const touchStartX = useRef(null)
  const touchStartY = useRef(null)
  const [slideDir, setSlideDir] = useState(null) // 'left' | 'right' | null
  const animating = useRef(false)

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
  }, [])

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current === null || animating.current) return

    const dx = e.changedTouches[0].clientX - touchStartX.current
    const dy = e.changedTouches[0].clientY - touchStartY.current
    const absDx = Math.abs(dx)
    const absDy = Math.abs(dy)

    // Require horizontal swipe of at least 30% of screen width
    // and more horizontal than vertical movement (not a scroll)
    const threshold = window.innerWidth * 0.30
    if (absDx < threshold || absDy > absDx) {
      touchStartX.current = null
      touchStartY.current = null
      return
    }

    const currentIndex = SWIPE_ORDER.indexOf(activePage)
    if (currentIndex === -1) {
      touchStartX.current = null
      touchStartY.current = null
      return
    }

    let nextIndex = null
    if (dx < 0 && currentIndex < SWIPE_ORDER.length - 1) {
      // Swipe left → next page
      nextIndex = currentIndex + 1
    } else if (dx > 0 && currentIndex > 0) {
      // Swipe right → previous page
      nextIndex = currentIndex - 1
    }

    if (nextIndex !== null) {
      animating.current = true
      const direction = dx < 0 ? 'left' : 'right'
      setSlideDir(direction)

      setTimeout(() => {
        onNavigate(SWIPE_ORDER[nextIndex])
        setSlideDir(null)
        animating.current = false
      }, 250)
    }

    touchStartX.current = null
    touchStartY.current = null
  }, [activePage, onNavigate])

  return { handleTouchStart, handleTouchEnd, slideDir }
}

function Portal() {
  const isMobile = useIsMobile()
  const [activePage, setActivePage] = useState('dashboard')
  const [showMore, setShowMore] = useState(false)
  const [userEmail, setUserEmail] = useState('local')
  useAvatars() // preload avatar cache

  useEffect(() => {
    async function getUser() {
      try {
        const cookie = document.cookie.split(';').find(c => c.trim().startsWith('CF_Authorization='))
        if (cookie) {
          const token = cookie.trim().replace('CF_Authorization=', '')
          const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
          if (payload.email) { setUserEmail(payload.email); return }
        }
      } catch {}
      const cached = localStorage.getItem('bf_user_email')
      setUserEmail(cached || 'local')
    }
    getUser()
  }, [])

  function navigate(page) {
    setActivePage(page)
    setShowMore(false)
  }

  const { handleTouchStart, handleTouchEnd, slideDir } = useSwipeNav(activePage, navigate)

  const pageComponents = {
    dashboard: <Dashboard onNavigate={navigate} />,
    daily: <DailyLog />,
    fitness: <Fitness />,
    calendar: <Calendar />,
    budget: <Budget />,
    photos: <Photos />,
    lists: <Lists />,
    social: <Social />,
  }

  const allNavItems = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'daily', label: 'Daily' },
    { id: 'fitness', label: 'Fitness' },
    { id: 'social', label: 'Social' },
    { id: 'calendar', label: 'Calendar' },
    { id: 'budget', label: 'Budget' },
    { id: 'photos', label: 'Photos' },
    { id: 'lists', label: 'Lists' },
  ]

  // Slide animation styles
  const slideStyle = slideDir ? {
    transform: slideDir === 'left' ? 'translateX(-40px)' : 'translateX(40px)',
    opacity: 0,
    transition: 'transform 250ms ease, opacity 250ms ease',
  } : {
    transform: 'translateX(0)',
    opacity: 1,
    transition: 'transform 250ms ease, opacity 250ms ease',
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', paddingBottom: isMobile ? '80px' : '0' }}>

      {/* Desktop top navbar */}
      {!isMobile && (
        <nav className="navbar">
          <h1>Bailey Family Portal</h1>
          <div className="nav-links">
            {allNavItems.map(item => (
              <button
                key={item.id}
                onClick={() => navigate(item.id)}
                className={activePage === item.id ? 'active' : ''}
              >
                {item.label}
              </button>
            ))}
            <button
              className="logout-btn"
              onClick={() => { window.location.href = 'https://baileyfamit.cloudflareaccess.com/cdn-cgi/access/logout' }}
            >
              Logout
            </button>
          </div>
        </nav>
      )}

      {/* Mobile top header */}
      {isMobile && (
        <div style={{ background: '#1a1a2e', color: 'white', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Avatar email={userEmail} size={36} userEmail={userEmail} showUploadOnTap={true} />
            <h1 style={{ fontSize: '17px', fontWeight: '700', letterSpacing: '-0.3px' }}>Bailey Family</h1>
          </div>
          <button
            onClick={() => { window.location.href = 'https://baileyfamit.cloudflareaccess.com/cdn-cgi/access/logout' }}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}
          >
            Logout
          </button>
        </div>
      )}

      {/* Page content — swipe handlers on mobile only */}
      <main
        className="content"
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
        <div style={isMobile ? slideStyle : undefined}>
          {pageComponents[activePage]}
        </div>
      </main>

      {/* Mobile bottom tab bar */}
      {isMobile && (
        <>
          {/* More drawer */}
          {showMore && (
            <div
              style={{ position: 'fixed', bottom: '72px', left: 0, right: 0, background: 'white', borderTop: '1px solid #f0f0f0', padding: '12px 16px', zIndex: 200, boxShadow: '0 -4px 20px rgba(0,0,0,0.1)' }}
              onClick={() => setShowMore(false)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                {MORE_ITEMS.map(item => (
                  <button
                    key={item.id}
                    onClick={() => navigate(item.id)}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', border: 'none', cursor: 'pointer', padding: '8px 16px', borderRadius: '12px', background: activePage === item.id ? '#fff0f2' : 'transparent' }}
                  >
                    <span style={{ fontSize: '24px' }}>{item.icon}</span>
                    <span style={{ fontSize: '12px', fontWeight: '500', color: activePage === item.id ? '#e94560' : '#3a3a3c' }}>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Bottom tab bar */}
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white', borderTop: '1px solid #f0f0f0', display: 'flex', zIndex: 300, paddingBottom: 'env(safe-area-inset-bottom)', boxShadow: '0 -1px 0 rgba(0,0,0,0.08)' }}>
            {NAV_ITEMS.map(item => {
              const isActive = item.id === 'more'
                ? showMore || MORE_ITEMS.some(m => m.id === activePage)
                : activePage === item.id

              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.id === 'more') {
                      setShowMore(!showMore)
                    } else {
                      navigate(item.id)
                    }
                  }}
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px', padding: '10px 4px', background: 'transparent', border: 'none', cursor: 'pointer', minHeight: '56px' }}
                >
                  <span style={{ fontSize: item.id === 'more' ? '18px' : '22px', lineHeight: 1, color: isActive ? '#e94560' : '#8e8e93', fontWeight: item.id === 'more' ? '700' : 'normal' }}>
                    {item.icon}
                  </span>
                  <span style={{ fontSize: '10px', fontWeight: '600', color: isActive ? '#e94560' : '#8e8e93', letterSpacing: '0.1px' }}>
                    {item.label}
                  </span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing onSignIn={() => window.location.href = '/portal'} />} />
        <Route path="/portal" element={<Portal />} />
      </Routes>
    </BrowserRouter>
  )
}

export default App
